import apiService from './apiService';

// Types mirror the FastAPI response shape (api/services/dmarc_service.py).

export interface DomainSummary {
  domain: string;
  report_count_30d: number;
  report_count_7d: number;
  messages_30d: number;
  messages_7d: number;
  pass_rate_30d: number | null;
  pass_rate_prior_30d: number | null;
  trend: 'up' | 'down' | 'flat' | null;
  last_seen_epoch: number | null;
  last_seen_iso: string | null;
}

export interface TimeseriesPoint {
  date: string;
  total_messages: number;
  pass_dmarc: number;
  pass_dkim: number;
  pass_spf: number;
  pass_rate: number | null;
}

export type SourceKind = 'direct' | 'forwarded' | 'unsigned' | 'unauthorized';

export interface SourceKindBreakdown {
  direct: number;
  forwarded: number;
  unsigned: number;
  unauthorized: number;
}

export interface SourceIp {
  source_ip: string;
  messages: number;
  pass_dmarc: number;
  fail: number;
  report_records?: number;
  domain_count?: number;
  domains?: string[];
  pass_rate: number | null;
  source_kind?: SourceKind;
  source_kind_breakdown?: SourceKindBreakdown;
}

export interface ReportListItem {
  domain: string;
  report_uid: string;
  reporting_org: string;
  date_begin: number;
  date_end: number;
  total_messages: number;
  pass_dmarc: number;
  pass_dkim: number;
  pass_spf: number;
}

export interface ReportRecord {
  source_ip: string;
  count: number;
  disposition?: string;
  dkim_aligned?: string;
  spf_aligned?: string;
  header_from?: string;
  dkim_result?: string;
  spf_result?: string;
  source_kind?: SourceKind;
}

export interface DmarcReport extends ReportListItem {
  report_id?: string;
  records: ReportRecord[];
  raw_s3_key: string | null;
  processed_at: string | null;
}

// Use the shared apiService so requests pick up auth cookies + CSRF.
export const dmarcApi = {
  listDomains: () =>
    apiService.get<{ domains: DomainSummary[] }>('/api/admin/dmarc/domains').then(r => r.data),
  getDomain: (domain: string, days = 30) =>
    apiService
      .get<{ summary: DomainSummary; timeseries: TimeseriesPoint[]; top_source_ips: SourceIp[] }>(
        `/api/admin/dmarc/domains/${encodeURIComponent(domain)}?days=${days}`,
      )
      .then(r => r.data),
  listReports: (domain: string, limit = 50) =>
    apiService
      .get<{ reports: ReportListItem[] }>(
        `/api/admin/dmarc/domains/${encodeURIComponent(domain)}/reports?limit=${limit}`,
      )
      .then(r => r.data),
  getReport: (domain: string, reportUid: string) =>
    apiService
      .get<DmarcReport>(
        `/api/admin/dmarc/domains/${encodeURIComponent(domain)}/reports/${encodeURIComponent(reportUid)}`,
      )
      .then(r => r.data),
  listSources: (days = 30, limit = 100) =>
    apiService
      .get<{ sources: SourceIp[] }>(`/api/admin/dmarc/sources?days=${days}&limit=${limit}`)
      .then(r => r.data),
  listMonitored: () =>
    apiService.get<{ domains: string[] }>('/api/admin/dmarc/monitored-domains').then(r => r.data),
};

// Formatting helpers ported from dmarc.travelminds.com's dmarc-utils.ts

export function fmtPct(rate: number | null | undefined): string {
  if (rate == null) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

export function fmtNumber(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString();
}

export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export function fmtEpoch(epoch: number | null | undefined): string {
  if (!epoch) return '—';
  return new Date(epoch * 1000).toISOString().replace('T', ' ').slice(0, 16) + 'Z';
}

/** Yellow = >=10pp pass-rate drop vs prior period. Red = >=20pp. */
export function anomalyLevel(
  current: number | null,
  prior: number | null,
): 'none' | 'warning' | 'critical' {
  if (current == null || prior == null) return 'none';
  const dropPp = (prior - current) * 100;
  if (dropPp >= 20) return 'critical';
  if (dropPp >= 10) return 'warning';
  return 'none';
}
